import { useContext, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Anchor, Banner, Box, CloseButton, Page, Text, TextButton } from '@zillow/constellation-10';
import BlockedUserModal from '@zg-rentals/blocked-user-modal';
import { CustomizedBlockedUserModal } from '@zg-rentals/satellite-components';
import type { SystemStyleObject } from '@/styled-system/types';
import { PageHeaderAsync } from '@zillow/page-frame';
import { styledComponentSet } from '@zillow/page-frame/styledComponentSet';
import MyZillowSubnav from '@zillow/my-zillow-subnav';
import { AppContext } from '../../contexts/AppContextProvider';
import HotpadsHeaderNav from '../../components/HotpadsHeaderNav';
import type { User } from '../../AuthProvider';
import useGetFeatureFlagsAndSettings from '../../hooks/useGetFeatureFlagsAndSettings';
import {
  BANNER_VIEW_EVENT_TYPE_ID,
  emitBannerViewEvent,
  emitSendToZrmInboxEvent,
  SEND_TO_ZRM_INBOX_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/inboxIA';
import { isHotpads, isZillow } from '@zg-rentals/multibrand-support';
import getPandaThemeAttributes from '../../utils/getPandaThemeAttributes';
import useWindowSize from '../../hooks/useWindowSize';

type Props = WithChildren<{
  user?: User;
  showHeader?: boolean;
  pageWrapperStyles?: SystemStyleObject;
  mainContentStyles?: SystemStyleObject;
}>;

const RENTAL_MANAGER_BANNER_INBOX_COOKIE = 'hasSeenRentalManagerBanner';

export default function Layout({
  children,
  user,
  showHeader = true,
  pageWrapperStyles = {},
  mainContentStyles = {},
}: Props) {
  const { brand } = useContext(AppContext);
  const [cookies, setCookies] = useCookies([RENTAL_MANAGER_BANNER_INBOX_COOKIE]);
  const hasSeenRentalManagerInboxCookie = cookies[RENTAL_MANAGER_BANNER_INBOX_COOKIE];
  const { features } = useGetFeatureFlagsAndSettings();
  const { canAccessBlockedUserModalFromRJSModule, enableInboxIA } = features;
  const { email, firstName, loggedIn, isUserBlocked, restrictions } = user || {};
  const isAllowed = restrictions?.isAllowed || false;
  const isHotpadsBrand = isHotpads(brand);
  const { isMobileView } = useWindowSize();
  const [shouldDisplayCustomizedBlockedUserModal, setShouldDisplayCustomizedBlockedUserModal] = useState(false);
  const [shouldDisplayBlockedUserModalFromModule, setShouldDisplayBlockedUserModalFromModule] = useState(false);

  useEffect(() => {
    if (isUserBlocked) {
      if (canAccessBlockedUserModalFromRJSModule && !isAllowed) {
        setShouldDisplayCustomizedBlockedUserModal(false);
        setShouldDisplayBlockedUserModalFromModule(true);
      } else if (!canAccessBlockedUserModalFromRJSModule) {
        setShouldDisplayCustomizedBlockedUserModal(true);
        setShouldDisplayBlockedUserModalFromModule(false);
      }
    }
  }, [canAccessBlockedUserModalFromRJSModule, isUserBlocked, isAllowed]);

  useEffect(() => {
    if (showHeader && isZillow(brand) && enableInboxIA && !hasSeenRentalManagerInboxCookie && user) {
      emitBannerViewEvent(user);
    }
  }, [showHeader, brand, enableInboxIA, hasSeenRentalManagerInboxCookie, user]);

  // Set HTML tag attributes for Panda theme
  useEffect(() => {
    if (brand) {
      const attributes = getPandaThemeAttributes(brand);
      const htmlElement = document.documentElement;
      Object.entries(attributes).forEach(([key, value]) => {
        if (value) {
          htmlElement.setAttribute(key, value);
        } else {
          htmlElement.removeAttribute(key);
        }
      });
    }
  }, [brand]);

  const handleCloseRentalManagerInboxBanner = () => {
    setCookies(RENTAL_MANAGER_BANNER_INBOX_COOKIE, 'true', {
      path: '/',
    });
  };

  const handleBannerClickToZrmInbox = (): void => {
    if (user) emitSendToZrmInboxEvent('zrm_inbox_banner', user);
    window.location.href = '/rental-manager/inbox';
  };

  const getBannerText = () => {
    const linkText = 'Zillow Rental Manager';

    if (isMobileView) {
      return {
        prefix: 'Open ',
        suffix: ' to read messages from your renters.',
        linkText,
      };
    }

    return {
      prefix: `To view messages from your renters, you'll need to open `,
      suffix: '.',
      linkText,
    };
  };

  const bannerText = getBannerText();

  return (
    <Box css={{ height: '100%', ...pageWrapperStyles }}>
      {/* use the blocked user modal from the blocked-user-modal module, default to use our version if the feature flag value is false */}
      {shouldDisplayCustomizedBlockedUserModal && <CustomizedBlockedUserModal brand={brand} />}
      {shouldDisplayBlockedUserModalFromModule && user != null && <BlockedUserModal user={user} />}

      {/* TODO: Replace @zillow/page-frame with @zillow/page-frame-next instead to render the zillow header nav */}
      {showHeader && isZillow(brand) && (
        <>
          {enableInboxIA && !hasSeenRentalManagerInboxCookie && (
            <Banner
              closeButton={<CloseButton onImpact={true} />}
              onClose={handleCloseRentalManagerInboxBanner}
              tone="info"
              css={{
                display: 'flex',
                alignItems: 'center',
              }}
              data-eventid={BANNER_VIEW_EVENT_TYPE_ID}
              data-eventdescription="Banner view"
            >
              <Text css={{ color: 'white' }}>
                {bannerText.prefix}
                <Anchor asChild>
                  <TextButton
                    tone="neutral"
                    textStyle="body"
                    onClick={handleBannerClickToZrmInbox}
                    onImpact={true}
                    data-eventid={SEND_TO_ZRM_INBOX_EVENT_TYPE_ID}
                  >
                    {bannerText.linkText}
                  </TextButton>
                </Anchor>
                {bannerText.suffix}
              </Text>
            </Banner>
          )}
          <PageHeaderAsync mainAsync componentSet={styledComponentSet} gaEventHandler={() => undefined} />
          <MyZillowSubnav activeTab={'Inbox'} />
        </>
      )}
      {showHeader && isHotpadsBrand && (
        <HotpadsHeaderNav email={email || ''} loggedIn={loggedIn || false} firstName={firstName || ''} />
      )}
      <Page.Root
        asChild
        css={{
          backgroundColor: {
            base: 'bg.screen.softest',
            _hotpadsTheme: 'transparent',
          },
          paddingBottom: '0',
          height: '100%',
          ...mainContentStyles,
        }}
      >
        <main>{children}</main>
      </Page.Root>
    </Box>
  );
}
